import { formatLocalDate } from '@/utils/localDateUtils';
import { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import CfeStatusBadge from './CfeStatusBadge';
import dgiService from '@/services/dgiService';
import { CfeDocument, CfeTipoLabel } from '@/types/cfe';
import { useDialog } from '@/hooks/useDialog';
import { formatReceiptAmount } from '@/utils/currencyUtils';
import { formatDgiReason, getCfeRequestError, normalizeCfeMessage } from '@/utils/cfeUtils';
import { useAuth } from '@/contexts/AuthContext';
import { buildCfeThermalHtml, showCfeThermalPreview } from '@/utils/cfeThermalPrint';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  cfe: CfeDocument | null;
  onCfeUpdated?: () => void;
};

export default function CfeDetailModal({ isOpen, onClose, cfe, onCfeUpdated }: Props) {
  const { showAlert, showConfirm } = useDialog();
  const { hasPermission } = useAuth();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [regularizing, setRegularizing] = useState(false);
  const [preparingPrint, setPreparingPrint] = useState(false);

  if (!cfe) return null;

  const tipoLabel = CfeTipoLabel[cfe.cfe_tipo_code] || `Tipo ${cfe.cfe_tipo_code}`;
  const errorMessage = normalizeCfeMessage(cfe.error_message);
  const warningMessage = normalizeCfeMessage(cfe.warning_message);

  const handleThermalPrint = async () => {
    if (preparingPrint) return;
    const popup = window.open('', '_blank', 'width=480,height=800');
    if (!popup) {
      showAlert({ type: 'error', title: 'Impresión térmica', message: 'Permití las ventanas emergentes para abrir el e-ticket.' });
      return;
    }
    popup.opener = null;
    popup.document.title = 'Preparando e-ticket';
    popup.document.body.textContent = 'Preparando e-ticket…';
    setPreparingPrint(true);
    try {
      // List/status responses omit XML; always fetch the saved fiscal snapshots.
      const { data: document } = await dgiService.getDocument(Number(cfe.id));
      showCfeThermalPreview(popup, await buildCfeThermalHtml(document));
    } catch (error) {
      popup.close();
      showAlert({ type: 'error', title: 'Impresión térmica',
        message: getCfeRequestError(error, 'No se pudo preparar el e-ticket para imprimir.') });
    } finally {
      setPreparingPrint(false);
    }
  };

  const handleRegularize = async () => {
    if (regularizing) return;
    setRegularizing(true);
    try {
      const { data } = await dgiService.previewRegularization(Number(cfe.repair_original_id || cfe.id));
      if (data.finished) {
        showAlert({ type: 'success', title: 'Regularización completada',
          message: `El reemplazo ${data.replacement_serie}-${data.replacement_numero} de ${data.serie}-${data.numero} fue aceptado por DGI. El original conserva su rechazo y sus respuestas.` });
        return;
      }
      if (data.execution_enabled === false) {
        showAlert({ type: 'info', title: 'Regularización pendiente de habilitación',
          message: 'La reemisión en producción está deshabilitada hasta validar y habilitar este flujo.' });
        return;
      }
      const confirmed = await showConfirm({
        title: data.can_start ? 'Reemitir resguardo rechazado' : 'Consultar regularización en curso',
        message: data.can_start
          ? `Resguardo ${data.serie}-${data.numero}, ${data.receptor}, ${data.moneda} ${data.importe}, emitido el ${data.fecha_emision}. Se emitirá un NUEVO resguardo con otro número fiscal, corrigiendo el indicador 9 a 0 y conservando la fecha original, receptor e importes. El original seguirá rechazado y quedará vinculado al nuevo. La reparación finalizará al confirmar su aceptación por DGI. ¿Continuar?`
          : `El intento de ${data.serie}-${data.numero} está en estado ${data.phase}. ${data.error || ''} Reemplazo: ${data.replacement_serie && data.replacement_numero ? `${data.replacement_serie}-${data.replacement_numero}` : "pendiente de identificar"}. Se consultará su resultado sin repetir una firma o envío inciertos. ¿Continuar?`,
        type: 'info',
      });
      if (!confirmed) return;
      const result = await dgiService.regularizeResguardo(Number(cfe.repair_original_id || cfe.id), data.original_sha256, !data.can_start);
      onCfeUpdated?.();
      onClose();
      showAlert({ type: result.success ? (result.phase === 'ACEPTADA' ? 'success' : 'info') : 'error',
        title: 'Regularización de resguardo', message: result.message });
    } catch (error) {
      showAlert({ type: 'error', title: 'Regularización de resguardo',
        message: getCfeRequestError(error, 'No se pudo regularizar el resguardo') });
    } finally {
      setRegularizing(false);
    }
  };

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      const result = await dgiService.syncStatus(Number(cfe.id));
      if (!result.success) throw new Error(result.message || 'No se pudo consultar el estado');
      onCfeUpdated?.();
      onClose();
    } catch (error) {
      showAlert({ type: 'error', title: 'Consulta de estado',
        message: getCfeRequestError(error, 'No se pudo consultar el estado en Taface') });
    } finally {
      setChecking(false);
    }
  };

  const handleEnviar = async () => {
    const confirmed = await showConfirm({
      title: 'Enviar CFE a DGI',
      message: '¿Confirma el envío de este CFE a DGI?',
      type: 'info',
    });
    if (!confirmed) return;

    setSending(true);
    try {
      const result = await dgiService.enviar(Number(cfe.id));
      const res = (result as any);
      if (res.success) {
        showAlert({
          type: 'success',
          title: 'Éxito',
          message: 'CFE enviado exitosamente',
        });
        onCfeUpdated?.();
        onClose();
      } else {
        showAlert({
          type: 'error',
          title: 'Error',
          message: res.message || 'Error al enviar',
        });
      }
    } catch (error) {
      console.error('Error sending CFE:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al enviar el CFE',
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await dgiService.downloadPdf(Number(cfe.id));
      if (!blob) return;

      // Do not save an HTML/JSON error response with a .pdf extension.
      // This can happen when the backend reports an error with HTTP 200.
      const header = await blob.slice(0, 5).text();
      if (header !== '%PDF-') {
        let message = 'La respuesta del servidor no es un PDF válido';
        try {
          const body = JSON.parse(await blob.text());
          message = body.message || message;
        } catch {
          // Keep the generic message for non-JSON responses (e.g. PHP HTML errors).
        }
        throw new Error(message);
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cfe-${cfe.cfe_serie || ''}-${cfe.cfe_numero || cfe.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al descargar el PDF',
      });
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Detalle CFE - ${tipoLabel}`} size="lg">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {cfe.cfe_serie && cfe.cfe_numero
                ? `${cfe.cfe_serie}-${cfe.cfe_numero}`
                : `Doc #${cfe.id}`}
            </h3>
            <p className="text-sm text-gray-500">{tipoLabel} (Cod. {cfe.cfe_tipo_code})</p>
          </div>
          <CfeStatusBadge cfe={cfe} showType={false} />
        </div>

        {cfe.repair_replacement_id && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            <p>Resguardo rechazado: {cfe.repair_original_number}. Reemplazo: {cfe.repair_replacement_number}.</p>
            <p>Reparación: {cfe.repair_phase === 'ACEPTADA' ? 'Completada con aceptación de DGI' : 'Pendiente de resolución'}.</p>
          </div>
        )}

        {/* Amounts */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Montos</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Gravado</p>
              <p className="text-sm font-medium">{formatReceiptAmount(cfe.monto_gravado, cfe.moneda)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">IVA</p>
              <p className="text-sm font-medium">{formatReceiptAmount(cfe.monto_iva, cfe.moneda)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-sm font-bold">{formatReceiptAmount(cfe.monto_total, cfe.moneda)}</p>
            </div>
          </div>
        </div>

        {/* Receptor */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Receptor</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nombre</p>
              <p className="text-sm font-medium">{cfe.receptor_nombre || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Documento</p>
              <p className="text-sm font-medium">{cfe.receptor_documento || '-'}</p>
            </div>
          </div>
        </div>

        {/* CFE Details */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Datos CFE</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Recibo</p>
              <p className="text-sm font-medium">{cfe.numero_recibo || `#${cfe.recibo_id}`}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">CAE</p>
              <p className="text-sm font-medium">{cfe.cfe_cae_autorizacion || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Fecha Creación</p>
              <p className="text-sm font-medium">{formatDate(cfe.created_at)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Fecha Envío</p>
              <p className="text-sm font-medium">{formatDate(cfe.cfe_fecha_envio)}</p>
            </div>
          </div>
        </div>

        {Boolean(cfe.cfe_confirmado) && (
          <div className="border-t border-gray-200 pt-4 space-y-2">
            <p className="text-sm"><strong>Estado en Taface:</strong> {cfe.dgi_estado_taface || 'Sin consultar'}</p>
            <p className="text-sm text-gray-500">Última consulta exitosa: {formatDate(cfe.dgi_fecha_consulta)}</p>
            {cfe.dgi_motivo_rechazo && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm font-medium text-red-800">Motivo informado por Taface/DGI</p>
                <p className="text-sm text-red-800 whitespace-pre-wrap break-words">{formatDgiReason(cfe.dgi_motivo_rechazo)}</p>
              </div>
            )}
            {cfe.dgi_error_consulta && <p role="alert" className="text-sm text-amber-800">No se pudo actualizar el estado: {cfe.dgi_error_consulta}. Se conserva el último resultado conocido.</p>}
          </div>
        )}
        {/* Error/Warning */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800"><strong>Error:</strong> {errorMessage}</p>
          </div>
        )}
        {warningMessage && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800"><strong>Advertencia:</strong> {warningMessage}</p>
          </div>
        )}

        {/* Actions */}
        <div className="border-t border-gray-200 pt-4 flex flex-wrap gap-2">
          {Number(cfe.cfe_tipo_code) === 101 && Boolean(Number(cfe.cfe_firmado_ok)) && Boolean(Number(cfe.cfe_confirmado)) &&
            !['RECHAZADO', 'ANULADO', 'RECHAZADO_O_ANULADO'].includes(cfe.dgi_estado || '') && (
            <Button onClick={handleThermalPrint} disabled={preparingPrint} variant="secondary">
              {preparingPrint ? 'Preparando e-ticket…' : 'Imprimir e-ticket térmico'}
            </Button>
          )}
          {cfe.cfe_tipo_code === 182 && (Boolean(cfe.cfe_confirmado) || Boolean(cfe.repair_original_id)) && hasPermission('dgi', 'write') && (
            <Button onClick={handleRegularize} disabled={regularizing || checking}>
              {regularizing ? 'Procesando...' : 'Reemitir / consultar reparación'}
            </Button>
          )}
          {Boolean(cfe.cfe_firmado_ok) && !Boolean(cfe.cfe_confirmado) && !cfe.repair_replacement_id && (
            <Button onClick={handleEnviar} disabled={sending}>
              {sending ? 'Enviando...' : 'Enviar a DGI'}
            </Button>
          )}
          {Boolean(cfe.cfe_confirmado) && hasPermission('dgi', 'write') && (
            <Button onClick={handleCheckStatus} disabled={checking || regularizing} variant="secondary">
              {checking ? 'Consultando...' : 'Consultar estado en Taface'}
            </Button>
          )}
          {Boolean(cfe.cfe_confirmado) && (
            <Button onClick={handleDownloadPdf} variant="secondary">
              Descargar PDF DGI
            </Button>
          )}
          <Button onClick={onClose} variant="secondary">
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
